import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RatingStars from "@/components/RatingStars";
import { Hotel, Plus, MapPin, Search } from "lucide-react";

export default function Hotels() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("rating");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    name: "", city: "", state: "", address: "", phone: "", website: "", notes: "", photo_url: "",
  });
  const [addError, setAddError] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/hotels", { params: { city: city || undefined, q: q || undefined, sort } });
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city, sort]);
  useEffect(() => { api.get("/hotels/cities").then((r) => setCities(r.data || [])).catch(() => {}); }, []);

  const submitSearch = (e) => { e.preventDefault(); load(); };

  const addHotel = async (e) => {
    e.preventDefault();
    setAddError("");
    if (!form.name || !form.city) { setAddError(t("hotels.form_required_error")); return; }
    setAdding(true);
    try {
      await api.post("/hotels", form);
      setShowAdd(false);
      setForm({ name: "", city: "", state: "", address: "", phone: "", website: "", notes: "", photo_url: "" });
      await load();
    } catch (e) {
      setAddError(e.response?.data?.detail || "Error al agregar hotel");
    } finally { setAdding(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="hotels-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline mb-3">Directorio</div>
          <h1 className="font-serif text-5xl font-light" style={{ color: "var(--text-primary)" }}>
            {t("hotels.title_main")} <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>{t("hotels.title_emph")}</em>
          </h1>
          <p className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>
            {t("hotels.subtitle")}
          </p>
        </div>
        {user ? (
          <button className="btn-primary inline-flex items-center gap-2" onClick={() => setShowAdd(true)} data-testid="hotels-add-btn">
            <Plus size={14} /> {t("hotels.add_btn")}
          </button>
        ) : (
          <Link to="/login" className="btn-primary inline-flex items-center gap-2" data-testid="hotels-add-btn-login">
            <Plus size={14} /> {t("hotels.login_to_add")}
          </Link>
        )}
      </div>

      {/* Filters */}
      <form onSubmit={submitSearch} className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
          <input
            className="input-field pl-9 w-full"
            placeholder={t("hotels.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="hotels-search"
          />
        </div>
        <select className="input-field" value={city} onChange={(e) => setCity(e.target.value)} data-testid="hotels-city-filter">
          <option value="">{t("hotels.all_cities")}</option>
          {cities.map((c) => (
            <option key={c.city} value={c.city}>{c.city} ({c.count})</option>
          ))}
        </select>
        <select className="input-field" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="hotels-sort">
          <option value="rating">{t("hotels.sort_rating")}</option>
          <option value="recent">{t("hotels.sort_recent")}</option>
          <option value="name">{t("hotels.sort_name")}</option>
        </select>
        <button type="submit" className="btn-secondary" data-testid="hotels-search-btn">{t("hotels.search")}</button>
      </form>

      {loading ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16" style={{ background: "var(--bg-surface)", border: "1px dashed var(--border-accent)" }} data-testid="hotels-empty">
          <Hotel size={40} strokeWidth={1} color="var(--brand-primary)" className="mx-auto mb-4" />
          <p className="font-serif text-2xl mb-2" style={{ color: "var(--text-primary)" }}>
            {t("hotels.empty_title")}
          </p>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {t("hotels.empty_subtitle")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hotels-grid">
          {items.map((h) => (
            <Link
              key={h.id}
              to={`/hoteles/${h.slug}`}
              className="group block p-5 transition-all"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
              data-testid={`hotel-card-${h.slug}`}
            >
              {h.photo_url && (
                <div className="-mx-5 -mt-5 mb-4 overflow-hidden" style={{ aspectRatio: "16/9" }}>
                  <img src={h.photo_url} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-serif text-xl leading-tight" style={{ color: "var(--text-primary)" }}>
                  {h.name}
                </h3>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <RatingStars value={h.rating_avg || 0} size={13} testid={`hotel-rating-${h.slug}`} />
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {h.rating_avg > 0 ? `${h.rating_avg.toFixed(1)} (${h.rating_count})` : t("hotels.no_reviews")}
                </span>
              </div>
              <p className="text-xs inline-flex items-center gap-1" style={{ color: "var(--text-secondary)" }}>
                <MapPin size={10} /> {h.city}{h.state && `, ${h.state}`}
              </p>
              {h.notes && (
                <p className="text-xs mt-2 line-clamp-2" style={{ color: "var(--text-muted)" }}>{h.notes}</p>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Add Hotel Modal */}
      {showAdd && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ background: "rgba(8,7,8,0.85)", backdropFilter: "blur(10px)" }}
          onClick={() => setShowAdd(false)}
          data-testid="hotels-add-modal"
        >
          <form
            onSubmit={addHotel}
            className="w-full max-w-lg p-8 flex flex-col gap-3 max-h-[90vh] overflow-y-auto"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--brand-primary)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-2xl mb-3" style={{ color: "var(--text-primary)" }}>{t("hotels.modal_title")}</h3>
            <input required placeholder={t("hotels.form_name")} className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="hotel-form-name" />
            <div className="grid grid-cols-2 gap-3">
              <input required placeholder={t("hotels.form_city")} className="input-field" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="hotel-form-city" />
              <input placeholder={t("hotels.form_state")} className="input-field" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <input placeholder={t("hotels.form_address")} className="input-field" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder={t("hotels.form_phone")} className="input-field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input placeholder={t("hotels.form_website")} className="input-field" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
            <input placeholder={t("hotels.form_photo")} className="input-field" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} />
            <textarea placeholder={t("hotels.form_notes")} className="input-field h-20 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            {addError && <p className="text-xs" style={{ color: "#ef6d6d" }} data-testid="hotel-form-error">{addError}</p>}
            <div className="flex gap-3 mt-2">
              <button type="button" className="btn-ghost flex-1" onClick={() => setShowAdd(false)}>{t("hotels.cancel")}</button>
              <button type="submit" className="btn-primary flex-1" disabled={adding} data-testid="hotel-form-submit">
                {adding ? t("hotels.saving") : t("hotels.add")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
